import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { notificationApi } from '../../services/api';
import './NotificationBell.css';

export type NotificationLink =
  | { page: 'results'; sessionId: string }
  | { page: 'question-management'; examType?: string }
  | { page: string; sessionId?: string; examType?: string };

interface NotificationItem {
  sk: string;
  notification_id: string;
  kind: 'insight-ready' | 'insight-failed' | 'ai-job-done' | 'ai-job-failed' | 'notice';
  title: string;
  body?: string;
  link?: NotificationLink;
  read: boolean;
  created_at: string;
}

const POLL_MS = 60_000;

const timeAgo = (iso: string) => {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return '방금';
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
};

const tone = (kind: NotificationItem['kind']) => (kind.endsWith('failed') ? 'is-failed' : kind === 'notice' ? 'is-notice' : 'is-done');

/**
 * 헤더 메시지함: 안 읽은 개수 배지 + 최근 알림 목록.
 * 60초마다(화면이 보일 때만), 그리고 창으로 돌아올 때 새로 불러옵니다.
 */
const NotificationBell: React.FC<{ onNavigate: (link: NotificationLink) => void }> = ({ onNavigate }) => {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const response = await notificationApi.list(30) as { data?: { items: NotificationItem[]; unread: number } };
      setItems(response.data?.items || []);
      setUnread(response.data?.unread || 0);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  // 바깥 클릭·Esc로 닫기
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await load();
      setLoading(false);
    }
  };

  const markRead = async (input: { ids?: string[]; all?: boolean }) => {
    // 화면은 먼저 바꾸고 서버는 뒤에서 처리
    setItems((current) => current.map((item) => (input.all || input.ids?.includes(item.sk) ? { ...item, read: true } : item)));
    setUnread((count) => (input.all ? 0 : Math.max(0, count - (input.ids?.length || 0))));
    try {
      await notificationApi.markRead(input);
    } catch {
      load();
    }
  };

  const openItem = (item: NotificationItem) => {
    if (!item.read) markRead({ ids: [item.sk] });
    setOpen(false);
    if (item.link) onNavigate(item.link);
  };

  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <div className="notif" ref={rootRef}>
      <button
        type="button"
        className={`notif__trigger ${open ? 'is-open' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread > 0 ? `메시지함, 안 읽은 알림 ${unread}개` : '메시지함'}
        title="메시지함"
      >
        <FiBell aria-hidden="true" />
        {unread > 0 && <span className="notif__badge" aria-hidden="true">{badge}</span>}
      </button>

      {open && (
        <div className="notif__panel" role="dialog" aria-label="메시지함">
          <div className="notif__head">
            <strong>메시지함</strong>
            {unread > 0 && (
              <button type="button" className="notif__all" onClick={() => markRead({ all: true })}>모두 읽음</button>
            )}
          </div>

          {error && items.length === 0 ? (
            <p className="notif__empty">알림을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</p>
          ) : loading && items.length === 0 ? (
            <p className="notif__empty">불러오는 중…</p>
          ) : items.length === 0 ? (
            <p className="notif__empty">새 알림이 없습니다. AI 분석이나 검토 작업이 끝나면 여기로 알려 드려요.</p>
          ) : (
            <ul className="notif__list">
              {items.map((item) => (
                <li key={item.sk}>
                  <button type="button" className={`notif__item ${item.read ? '' : 'is-unread'}`} onClick={() => openItem(item)}>
                    <span className={`notif__dot ${tone(item.kind)}`} aria-hidden="true" />
                    <span className="notif__text">
                      <span className="notif__title">{item.title}</span>
                      {item.body && <span className="notif__body">{item.body}</span>}
                      <span className="notif__time">{timeAgo(item.created_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
